<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Select } from 'ant-design-vue';

import { useAgentsQuery } from '@/graphql/generated/graphql';
import Loading from '@/ui/Loading.vue';

const modelValue = defineModel<number[]>('value', { default: () => [] });
const agentNameQuery = ref('');
const agentNamesById = ref(new Map<number, string>());

const syncSelectedAgentNames = (
  agents: Array<{ id: number; full_name: string }> | null | undefined,
) => {
  if (!agents?.length) {
    return;
  }

  const selectedAgentIds = new Set(modelValue.value);
  const nextAgentNamesById = new Map(agentNamesById.value);

  for (const agent of agents) {
    if (selectedAgentIds.has(agent.id)) {
      nextAgentNamesById.set(agent.id, agent.full_name);
    }
  }

  agentNamesById.value = nextAgentNamesById;
};

// Loads only when the component is mounted
const initialAgentIds = [...modelValue.value];
const { fetching: fetchingId, executeQuery: executeSelectedAgentsQuery } = useAgentsQuery({
  variables: {
    inputGenerated: {
      id: initialAgentIds.length > 0 ? initialAgentIds : undefined,
      idOperator: 'in',
    },
  },
  pause: true,
});

if (initialAgentIds.length > 0) {
  const response = await executeSelectedAgentsQuery({ requestPolicy: 'network-only' });
  syncSelectedAgentNames(response.data.value?.agents.data);
}

// Loads when the user starts typing in the search input
const isPaused = computed(() => !agentNameQuery.value || agentNameQuery.value.trim().length === 0);
const { data, fetching, error } = useAgentsQuery({
  variables: computed(() => ({
    search: {
      name: agentNameQuery.value,
    },
  })),
  pause: isPaused,
});

watch(modelValue, (selectedIds) => {
  const selectedAgentIds = new Set(selectedIds);

  agentNamesById.value = new Map(
    [...agentNamesById.value].filter(([id]) => selectedAgentIds.has(id)),
  );
}, { immediate: true });

watch([data, modelValue], ([newData]) => {
  syncSelectedAgentNames(newData?.agents.data);
}, { immediate: true });

const agents = computed(() => {
  const itemsByValue = new Map<number, { label: string; value: number }>();

  for (const selectedId of modelValue.value) {
    const selectedAgentName = agentNamesById.value.get(selectedId);

    if (selectedAgentName) {
      itemsByValue.set(selectedId, {
        label: selectedAgentName,
        value: selectedId,
      });
    }
  }

  for (const agent of data.value?.agents.data ?? []) {
    itemsByValue.set(agent.id, {
      label: agent.full_name,
      value: agent.id,
    });
  }

  return [...itemsByValue.values()];
});

const onSearch = (value: string) => {
  agentNameQuery.value = value;
};
</script>

<template>
  <Select
    v-model:value="modelValue"
    mode="multiple"
    :options="agents"
    :loading="fetching"
    :disabled="fetchingId"
    :status="error ? 'error' : undefined"
    show-search
    :filter-option="false"
    allow-clear
    placeholder="Search for an agent..."
    class="w-full"
    @search="onSearch"
  >
    <template #suffixIcon>
      <FaIcon :icon="['fal', 'user']" />
    </template>
    <template #notFoundContent>
      <Loading v-if="fetching" title="Loading agents..." />
      <span v-else>No agents found</span>
    </template>
  </Select>
</template>
